import "./AIMessage.css";
import { AgentChatResponseToolbar } from "./AgentChatResponseToolbar";
import { useSelector } from "react-redux";
import { useState, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import React from "react";

import { selectLinkRecords } from "../../redux/features/appData/appDataSlice";
import { showdownConverter } from "winslow-core";
import { LinkRecordActivity } from "../shares/LinkRecordActivity";
import { getStoreLinkRecord } from "winslow-core";
import { selectPersonas } from "../../redux/features/agent/agentSlice";
import { DeterminedPersonas } from "./DeterminedPersonas";

type AIMessageProps = {
  content: string;
  sources?: string[];
  personaIds?: string[];
};

export const AIMessageStreaming = React.memo(
  ({ content, sources, personaIds }: AIMessageProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [htmlContent, setHtmlContent] = useState<string>("");
    const [isStreaming, setIsStreaming] = useState<boolean>(true);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const linkRecords = useSelector(selectLinkRecords);

    // Debounced markdown conversion during streaming
    useEffect(() => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // During streaming, show raw text immediately and debounce HTML conversion
      debounceTimeoutRef.current = setTimeout(() => {
        const convertedHtml = showdownConverter.makeHtml(content);
        setHtmlContent(convertedHtml);
        setIsStreaming(false);
      }, 50); // Convert to HTML after 50ms of no updates for faster responsiveness

      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }, [content]);

    // Force immediate DOM updates for streaming content
    useLayoutEffect(() => {
      if (isStreaming && content) {
        // This ensures content renders immediately without waiting for React batching
        setIsStreaming(true);
      }
    }, [content, isStreaming]);

    // Memoize sources rendering to prevent unnecessary re-renders
    const sourcesContent = useMemo(() => {
      if (!sources || sources.length === 0) return null;

      return (
        <div className="agent-chat-q-and-a-preview-sources">
          {sources.map((linkRecordId) => {
            const linkRecord = getStoreLinkRecord(
              linkRecordId,
              linkRecords ?? []
            );
            if (!linkRecord) return null;

            return (
              <LinkRecordActivity
                key={linkRecordId}
                linkRecord={linkRecord}
                minimum={true}
                showDrag={false}
                clickIsOpen={true}
              />
            );
          })}
        </div>
      );
    }, [sources, linkRecords]);

    return (
      <div className="agent-chat-history-prompt-response">
        {personaIds && <DeterminedPersonas personaIds={personaIds} />}
        <div className={`agent-chat-history-prompt-response-object `}>
          <div className="agent-chat-history-general">
            <div className="agent-chat-q-and-a-preview">
              {sourcesContent}
              <div
                ref={contentRef}
                className="agent-chat-q-and-a-preview-content">
                {isStreaming ? (
                  // Show raw text during active streaming for immediate feedback
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      fontFamily: "inherit",
                      margin: 0,
                    }}>
                    {content}
                  </pre>
                ) : (
                  // Show formatted HTML when streaming pauses
                  <div
                    dangerouslySetInnerHTML={{
                      __html: htmlContent,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* <div className="agent-chat-history-item-toolbar">
        <AgentChatResponseToolbar
          key={`toolbar-${content}`}
          message={content}
        />
      </div> */}
      </div>
    );
  }
);
